import { Injectable } from '@angular/core'
import { fetchUserAttributes, fetchAuthSession, signOut } from 'aws-amplify/auth'
import { ToastrService } from 'ngx-toastr'
import { BehaviorSubject, Observable, from, of } from 'rxjs'
import { catchError, concatMap, delay, first, map, switchMap } from 'rxjs/operators'
import { LoaderService } from '../../services/loader.service'
import { GtmTrackingService } from 'src/app/services/gtm-tracking.service'
import { UserAuthService } from 'src/app/services/user-auth.service'
import { Router } from '@angular/router'
export const authToken = new BehaviorSubject<string>('')

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _token: any
  public get token (): string {
    return this._token
  }
  public set token (v: string) {
    this._token = v
    authToken.next(this._token)
  }
  constructor (private loaderService: LoaderService,
    private toastr: ToastrService, private gtmService: GtmTrackingService,
    private authService: UserAuthService, private router: Router) { }

  async refreshToken () {
    try {
      const session = await fetchAuthSession()
      const accessToken = session.tokens?.accessToken?.toString()
      if (accessToken) {
        this.token = accessToken
      } else {
        this.signOut()
      }
    } catch (e) {
      this.signOut()
    }
  }

  checkToken (): Observable<{ sub: string } | null> {
    return this.authenticatedUser().pipe(
      concatMap(({ isAuth: isAuthenticated }) => {
        if (isAuthenticated) {
          return new Observable<{ sub: string } | null>(observer => {
            (async () => {
              try {
                const session = await fetchAuthSession()
                const token = session.tokens?.accessToken?.toString()
                if (!token) {
                  observer.next(null)
                  return observer.complete()
                }
                this.token = token
                const attributes = await fetchUserAttributes()
                if (attributes.sub) {
                  observer.next({ sub: attributes.sub })
                } else {
                  observer.next(null)
                }
                observer.complete()
              } catch (error) {
                observer.next(null)
                observer.complete()
              }
            })()
          })
        } else {
          return of(null)
        }
      }),
      first()
    )
  }

  authenticatedUser (): Observable<{
    isAuth: boolean
    user: any
  }> {
    return of('').pipe(delay(1), switchMap(() => from(fetchUserAttributes()).pipe(
      first(),
      catchError((e) => {
        return of(false)
      }),
      map((user) => ({ isAuth: !!user, user }))
    )))
  }

  signOut (): void {
    this.loaderService.show()
    const defaultErrMsg = 'Oops .. something went wrong. Please try again later'
    signOut({ global: true }).then(() => {
        this.gtmService.setOrGetClientTracking()
        this.gtmService.setClientTrackingWithLoggedInUser()
        this.authService.setUserId('')
        this.authService.setIdpUser(false)
        this.authService.deleteCookie('referrerUrl')
        this.authService.deleteCookie('previousPageUrl')
        this.authService.deleteCognitoCookies()
        this.clearStorageValues()
        this.router.navigate(['/']).then(() => {
          window.location.reload()
        })
      }).catch((err: any) => this.toastr.error(err?.message || defaultErrMsg))
    .finally(() => this.loaderService.hide())
  }
  clearStorageValues () {
    try {
      localStorage.clear()
      sessionStorage.clear()
    } catch (error) {
      console.error('Error clearing storage values:', error)
    }
  }

  public fetchCurrentUserInfo (): Promise<any> {
    return fetchUserAttributes()
  }
}
